import { useCallback, useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './TamCoverage.module.css'

/**
 * TAM COVERAGE — every state, every segment, organizations AND the people.
 *
 * Brian: "we need to show all 50 states. i need them alphabetized. we should
 * see how many schools - how many coaches. this also goes for corporations.
 * police, fire, gvt, greek etc. our tam"
 *
 * ⚠️ THE RATIO IS THE POINT, NOT THE ORGANIZATION COUNT. Measured when this was
 * built: 291 police / fire / government departments with 28 people between them,
 * 206 Greek chapters with 53, 1,783 corporations with 1,170. An organization
 * with nobody attached cannot be sold to — there is no one to contact — so an
 * org count on its own is exactly the number that made the pipeline look
 * healthier than it is. Every count here is shown beside its people.
 *
 * ⚠️ ZEROS ARE RENDERED, NEVER OMITTED. A state with no records is the most
 * important row in the table: Oklahoma has zero companies of any kind, in any
 * segment. Dropping empty rows is precisely how that stayed invisible. The
 * table is built from a fixed list of 50 states + DC, not from whatever HubSpot
 * happened to return, so a missing state cannot silently disappear.
 *
 * The people column is the SCHOOL/COACH population specifically — it is what
 * the directory loader writes. It is labelled that way rather than presented as
 * "all people", because corporate and public-safety contacts carry no state of
 * their own and folding them in would overstate coverage.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')
const per = (people, orgs) => (!orgs || typeof people !== 'number' ? '—' : (people / orgs).toFixed(1))

/* Under this many people per organization, the segment is not workable — there
 * is nobody to call at most of them. Flagged rather than left for the reader to
 * compute across two columns. */
const THIN = 1

export default function TamCoverage() {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })

  const load = useCallback((force = false) => {
    setState((s) => ({ ...s, status: s.data ? 'refreshing' : 'loading' }))
    api.tamCoverage({ force })
      .then((d) => setState({ status: 'ready', data: d, error: '' }))
      .catch((e) => setState({ status: 'error', data: null, error: e?.body?.detail || e?.message || 'Could not read coverage.' }))
  }, [])

  useEffect(() => { if (allowed) load(false) }, [allowed, load])

  /* A snapshot still being computed reports itself, so a number that is being
   * recomputed never just looks frozen. */
  useEffect(() => {
    if (!allowed || !state.data?.refreshing) return undefined
    const t = setTimeout(() => load(false), 15000)
    return () => clearTimeout(t)
  }, [allowed, state.data?.refreshing, load])

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Coverage" title="TAM" />
        <div className="card"><p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p></div>
      </>
    )
  }

  const d = state.data
  const segs = d?.segments || []
  const totals = d?.totals

  return (
    <>
      <PageHeader
        eyebrow="Coverage"
        title="TAM"
        subtitle="Every state and every segment — how many organizations we hold, and how many people are actually attached to them."
      />

      {state.status === 'error' && <div className="card"><p className={styles.bad}>{state.error}</p></div>}

      {/* 🔴 THE LOADING BRANCH THAT WAS NEVER WRITTEN.
          This page initialises as `{ status: 'loading', data: null }` and the
          render path tested exactly three things — `error`, `d?.refreshing`
          and `totals`. With `data: null` every one of them is false, so for
          the entire duration of the fetch the page rendered its title and
          NOTHING ELSE: a heading on an empty cream field. On a phone on a slow
          connection that is indistinguishable from a broken screen, and it is
          the most literal version of "looks like a half assed wireframe" in
          the product — because for those seconds it genuinely is one.

          ⚠️ AND IT SAYS LOADING, NOT "no data". The distinction this whole
          page is careful about elsewhere (`row.coachPeople == null ? 'not
          read' : nf(...)`, because reporting a failed read as zero invents a
          gap) was missing at the top level. */}
      {state.status === 'loading' && (
        <div className="card">
          <p className={styles.muted}>
            <span className="spinner" /> Counting every state and segment — nothing is shown
            until the count finishes, because a partial count read as a total is how a state
            holding nothing looks like a state we do not sell into.
          </p>
        </div>
      )}

      {d?.refreshing && !d?.states?.length && (
        <div className="card"><p className={styles.muted}>{d.note || 'Counting…'}</p></div>
      )}

      {totals && (
        <section className="card">
          <div className={styles.head}>
            <h2 className={styles.h}>The whole picture</h2>
            <div className={styles.meta}>
              {d.generatedAt && <span>counted {new Date(d.generatedAt).toLocaleString()}</span>}
              {d.refreshing && <span className={styles.tag}>recounting</span>}
              <button className="btn" type="button" onClick={() => load(true)}>Recount</button>
            </div>
          </div>
          {/* An org count next to nothing is the thing worth seeing. */}
          <div className={styles.cards}>
            {segs.map((s) => {
              const orgs = totals[s.key] || 0
              /* Only the school segments have a per-state people figure, so a
                 per-segment ratio is honest for schools and unknown elsewhere —
                 shown as unknown rather than as zero. */
              const people = s.key === 'highSchool' ? totals.coachPeople : null
              return (
                <div key={s.key} className={styles.cardItem}>
                  <b>{nf(orgs)}</b>
                  <span>{s.label}</span>
                  {people != null && (
                    <em data-thin={per(people, orgs) !== '—' && Number(per(people, orgs)) < THIN ? '1' : '0'}>
                      {nf(people)} people · {per(people, orgs)} each
                    </em>
                  )}
                </div>
              )
            })}
          </div>
          <p className={styles.muted}>
            The people column counts the school and coach population the directory loader writes.
            Corporate and public-safety contacts carry no state of their own, so they are not folded
            in here — counting them would overstate coverage rather than measure it.
          </p>
        </section>
      )}

      {d?.states?.length > 0 && (
        <section className="card">
          <h2 className={styles.h}>By state · all 50 and DC, alphabetical</h2>
          <p className={styles.muted}>
            Every state has a row whether or not it holds anything. A state showing zeros is the
            work — Oklahoma holds no companies of any kind.
          </p>
          <div className={styles.scroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.sticky}>State</th>
                  {segs.map((s) => <th key={s.key}>{s.label}</th>)}
                  <th>All orgs</th>
                  <th>Coach people</th>
                </tr>
              </thead>
              <tbody>
                {d.states.map((row) => {
                  const empty = !row.orgs
                  /* ⚠️ DECLARED A READOUT, NOT FORGOTTEN. This is a state ×
                   * segment COUNT MATRIX — every cell is a number in a
                   * cross-tab, not a record. Giving the row a handler would mean
                   * inventing a destination, which is how three dead controls
                   * got shipped today. check-clickable now requires the
                   * declaration rather than silently accepting an inert row. */
                  return (
                    <tr key={row.state} data-empty={empty ? '1' : '0'} aria-disabled="true">
                      <th scope="row" className={styles.sticky}>{row.state}</th>
                      {segs.map((s) => (
                        <td key={s.key} data-zero={!row[s.key] ? '1' : '0'}>{nf(row[s.key])}</td>
                      ))}
                      <td><b>{nf(row.orgs)}</b></td>
                      {/* null means the read failed — that is not zero, and
                          reporting it as zero would invent a gap. */}
                      <td data-zero={!row.coachPeople ? '1' : '0'}>
                        {row.coachPeople == null ? 'not read' : nf(row.coachPeople)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th className={styles.sticky}>Total</th>
                  {segs.map((s) => <td key={s.key}><b>{nf(totals?.[s.key])}</b></td>)}
                  <td><b>{nf(totals?.orgs)}</b></td>
                  <td><b>{nf(totals?.coachPeople)}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}
    </>
  )
}
